import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { MenuKind, PublicMenuDto, PublicOrderDto } from '@tiffinify/contracts';
import { formatDay, orderLineText, waLink } from '@tiffinify/core';
import { Icon, PageLoader } from '../components/ui';
import { Amt, Back, Badge, Btn, Btn2, Card, Chip, Chips, Empty, Field, Foot, G2, Group, Hero, Ic, Kick, KV, Mk, Note, Row, Screen, Sm, Title, Top } from '../components/p';
import { post } from '../lib/api';
import { errorMessage, formatInr, mealName } from '../lib/format';
import { useKitchenPage, useMyOrder, usePublicMenu } from '../lib/queries';
import { KIND, closesText, daysText, weight } from './MenuScreens';

/* The customer's side: no app, no sign-in, on any phone. */

/** A plate from every day or next-day dinner goes with a meal; sweets and stock just go on the day. */
const isMeal = (kind: MenuKind | null) => kind === null || kind === 'everyday' || kind === 'nextday';

const KitchenChip = ({ name }: { name: string }) => (
  <span className="p-kchip" style={{ alignSelf: 'flex-start' }}><i aria-hidden="true">{name.slice(0, 1).toUpperCase()}</i><span>{name}</span></span>
);

function Unavailable({ what }: { what: string }) {
  return (
    <Screen gap={18}>
      <Card><Empty title={`This ${what} is not available`}>The link may be old. Ask the kitchen for a fresh one on WhatsApp.</Empty></Card>
      <Foot />
    </Screen>
  );
}

/** The kitchen's own page (P52): its open Menus and its monthly plans. */
export function KitchenPage() {
  const { code = '' } = useParams();
  const q = useKitchenPage(code);
  if (q.isError) return <Unavailable what="page" />;
  if (!q.data) return <PageLoader label="Opening the kitchen" />;
  const k = q.data;
  return (
    <Screen gap={16}>
      <Hero label={k.kitchenName} style={{ gap: 12, marginTop: 4 }}>
        <KitchenChip name={k.kitchenName} />
        <span style={{ fontFamily: 'var(--p-display)', fontSize: 28, fontWeight: 600, letterSpacing: '-0.045em', lineHeight: 1.05 }}>Home food, delivered to your door</span>
        <span className="p-muted">{window.location.host}/k/{code}</span>
      </Hero>
      <Group label="Order now">
        {k.menus.length === 0
          ? <Card><Empty title="Nothing open right now">Message the kitchen to ask what’s next.</Empty></Card>
          : <Card>{k.menus.map((m) => (
              <Row key={m.code} to={`/k/${code}/${m.code}`} left={<Ic icon="receipt" tone="s" />} t={m.name}
                s={`${m.closesAt ? closesText(m.closesAt) : 'Open now'} · delivery ${daysText(m.days)}`} right={<Badge tone="s">Open</Badge>} />
            ))}</Card>}
      </Group>
      {k.plans.length > 0 && (
        <Group label="Monthly plans">
          <Card>{k.plans.map((p) => <Row key={p.name} t={p.name} s={`${p.mealDays} meal days`} right={<Amt>{formatInr(p.price)}</Amt>} />)}</Card>
        </Group>
      )}
      {k.plans.length > 0 && <Btn label="Start a plan" to={k.registrationPath} />}
      {k.whatsapp && <Btn2 label="Message us on WhatsApp" icon="chat" href={waLink(k.whatsapp, `Hi ${k.kitchenName}`)} />}
      <Foot />
    </Screen>
  );
}

type Details = { name: string; phone: string; area: string; address: string };
const SAVED = 'tiffinify:customer';
const loadDetails = (): Details => {
  const empty = { name: '', phone: '', area: '', address: '' };
  try { return { ...empty, ...(JSON.parse(localStorage.getItem(SAVED) ?? '{}') as Partial<Details>) }; } catch { return empty; }
};
const amountOf = (i: PublicMenuDto['menu']['items'][number], qty: number) => (i.unit === 'kg' ? Math.round((i.price * qty) / 1000) : i.price * qty);

/** A Menu's link (P54 → P55): pick items and amounts, then where it goes and which day, then book. */
export function MenuShop() {
  const { code = '', menu = '' } = useParams();
  const q = usePublicMenu(code, menu);
  const nav = useNavigate();
  const [qty, setQty] = useState<Record<string, number>>({});
  const [step, setStep] = useState<'pick' | 'details'>('pick');
  const [det, setDet] = useState<Details>(loadDetails);
  const [day, setDay] = useState('');
  const [note, setNote] = useState('');

  const d = q.data;
  const m = d?.menu;
  const chosen = m ? m.items.filter((i) => (qty[i.id] ?? 0) > 0) : [];
  const itemsTotal = chosen.reduce((n, i) => n + amountOf(i, qty[i.id] ?? 0), 0);
  const charge = !m || itemsTotal === 0 ? 0 : m.freeAbove > 0 && itemsTotal >= m.freeAbove ? 0 : m.deliveryCharge;
  const total = itemsTotal + charge;
  const short = m && m.minTotal > itemsTotal ? m.minTotal - itemsTotal : 0;
  const grams = chosen.filter((i) => i.unit === 'kg').reduce((n, i) => n + (qty[i.id] ?? 0), 0);
  const deliverOn = day || m?.days[0] || '';

  const place = useMutation({
    mutationFn: () => post<PublicOrderDto>(`/k/${code}/${menu}/orders`, {
      name: det.name, phone: det.phone, area: det.area, address: det.address, day: deliverOn, note,
      lines: chosen.map((i) => ({ itemId: i.id, qty: qty[i.id] ?? 0 })),
    }),
    onSuccess: (o) => {
      try { localStorage.setItem(SAVED, JSON.stringify(det)); } catch { /* a private window keeps nothing; the order still went through */ }
      nav(`/o/${o.token}`, { state: { booked: true, tell: o.tell } });
    },
  });

  if (q.isError) return <Unavailable what="Menu" />;
  if (!d || !m) return <PageLoader label="Opening the Menu" />;
  const set = (id: string, n: number) => setQty((x) => ({ ...x, [id]: Math.max(0, n) }));

  if (step === 'details') {
    return (
      <Screen gap={18}>
        <Top><Back label="Menu" onClick={() => setStep('pick')} /></Top>
        <Title h="Where should it go?" sub="Saved on this phone for next time." />
        <G2>
          <Field label="Name" id="c-name" value={det.name} autoComplete="name" onChange={(v) => setDet({ ...det, name: v })} />
          <Field label="Phone" id="c-phone" value={det.phone} inputMode="tel" autoComplete="tel" onChange={(v) => setDet({ ...det, phone: v })} />
        </G2>
        {d.areas.length > 0 ? (
          <Group label="Community">
            <Chips label="Community">{d.areas.map((a) => <Chip key={a} on={det.area === a} onClick={() => setDet({ ...det, area: a })}>{a}</Chip>)}</Chips>
          </Group>
        ) : <Field label="Area or community" id="c-area" value={det.area} onChange={(v) => setDet({ ...det, area: v })} />}
        <Field label="Block, villa or flat" id="c-flat" value={det.address} placeholder="Phase 2, Villa 118" onChange={(v) => setDet({ ...det, address: v })} />
        {m.days.length > 1
          ? <Group label="Delivery day"><Chips label="Delivery day">{m.days.map((date) => <Chip key={date} on={deliverOn === date} onClick={() => setDay(date)}>{formatDay(date)}</Chip>)}</Chips></Group>
          : <Note icon="calendar">Delivery on {formatDay(deliverOn)}{isMeal(m.kind) ? `, ${mealName(m.meal).toLowerCase()}` : ''}.</Note>}
        <Field label="Note for the kitchen (optional)" id="c-note" value={note} placeholder="Call at the gate" onChange={setNote} />
        <Card pad style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <KV k={chosen.map((i) => orderLineText({ name: i.name, unit: i.unit, qty: qty[i.id] ?? 0 })).join(' · ')} v={formatInr(itemsTotal)} />
          <KV k="Delivery" v={charge ? formatInr(charge) : 'Free'} />
          <KV k="Total" v={formatInr(total)} />
        </Card>
        {place.error && <span className="p-error" role="alert">{errorMessage(place.error)}</span>}
        {d.reviewToday && deliverOn === d.today && <Note icon="clock">The kitchen needs to confirm this late order before it can go out.</Note>}
        <Btn label={`Place order · ${formatInr(total)}`} go="check" busy={place.isPending}
          disabled={!det.name.trim() || det.phone.replace(/\D/g, '').length < 10 || !deliverOn} onClick={() => place.mutate()} />
        <Foot />
      </Screen>
    );
  }

  return (
    <Screen gap={18}>
      <Top><KitchenChip name={d.kitchenName} /></Top>
      <Hero label={m.name} style={{ gap: 10 }}>
        <Kick>{m.name}</Kick>
        <span style={{ fontFamily: 'var(--p-display)', fontSize: 28, fontWeight: 600, letterSpacing: '-0.045em', lineHeight: 1.05 }}>{m.note || `${KIND[m.kind]}, made fresh`}</span>
        <div className="p-facts">
          <div><Kick>Order by</Kick><b>{m.closesAt ? closesText(m.closesAt).replace(/^Closes /, '') : 'Any time'}</b></div>
          <div><Kick>Delivered</Kick><b>{daysText(m.days)}</b></div>
        </div>
      </Hero>
      {!d.open && <Note icon="clock">Booking for this Menu has closed. Message the kitchen if you still need something.</Note>}
      {(m.minTotal > 0 || m.deliveryCharge > 0) && (
        <span className="p-sub">{[m.minTotal > 0 ? `Smallest order ${formatInr(m.minTotal)}` : null, m.deliveryCharge > 0 ? `Delivery ${formatInr(m.deliveryCharge)}${m.freeAbove ? `, free above ${formatInr(m.freeAbove)}` : ''}` : null].filter(Boolean).join(' · ')}</span>
      )}
      <Card>
        {m.items.map((i) => {
          const n = qty[i.id] ?? 0;
          const step1 = i.unit === 'kg' ? i.stepG : 1;
          const first = i.unit === 'kg' ? i.minG : 1;
          return (
            <Row key={i.id} style={i.soldOut ? { opacity: 0.45 } : undefined} left={<Mk food={i.food} />} t={i.name}
              s={`${formatInr(i.price)}${i.unit === 'kg' ? ' / kg' : ` a ${i.unit}`}${i.note ? ` · ${i.note}` : ''}`}
              right={i.soldOut ? <Badge>Sold out</Badge> : n > 0 ? (
                <span className="p-step">
                  <button type="button" aria-label={`Less ${i.name}`} onClick={() => set(i.id, n - step1 < first ? 0 : n - step1)}>–</button>
                  <b>{i.unit === 'kg' ? weight(n) : n}</b>
                  <button type="button" className="k" disabled={!d.open || (i.remaining != null && n + step1 > i.remaining)} aria-label={`More ${i.name}`} onClick={() => set(i.id, n + step1)}>+</button>
                </span>
              ) : <Sm label="Add" disabled={!d.open} onClick={() => set(i.id, first)} />} />
          );
        })}
      </Card>
      {chosen.length > 0 && <div style={{ height: 88 }} aria-hidden="true" />}
      {chosen.length > 0 && (
        <div className="p-dock">
          <Btn onClick={() => setStep('details')} disabled={short > 0 || !d.open} label={(
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
              <span style={{ fontSize: 12, color: '#B0A79A', fontWeight: 500 }}>
                {short > 0 ? `Add ${formatInr(short)} more (smallest order ${formatInr(m.minTotal)})` : [`${chosen.length} ${chosen.length === 1 ? 'item' : 'items'}`, grams ? weight(grams) : null, charge ? `delivery ${formatInr(charge)}` : null].filter(Boolean).join(' · ')}
              </span>
              <span>{formatInr(total)}</span>
            </span>
          )} />
        </div>
      )}
    </Screen>
  );
}

/** The customer's own order (P56 right after booking, P57 after): status, what it is, pay, and cancel while booking is open. */
export function MyOrder() {
  const { token = '' } = useParams();
  const state = (useLocation().state ?? {}) as { booked?: boolean; tell?: string | null };
  const qc = useQueryClient();
  const q = useMyOrder(token);
  const done = (o: PublicOrderDto) => qc.setQueryData(['o', token], o);
  const paid = useMutation({ mutationFn: () => post<PublicOrderDto>(`/o/${token}/paid`), onSuccess: done });
  const cancel = useMutation({ mutationFn: () => post<PublicOrderDto>(`/o/${token}/cancel`), onSuccess: done });
  const [sure, setSure] = useState(false);
  if (q.isError) return <Unavailable what="order" />;
  if (!q.data) return <PageLoader label="Opening your order" />;
  const o = q.data;
  const err = paid.error ?? cancel.error;
  const lines = (
    <Card>
      {o.lines.map((l) => <Row key={`${l.name}|${l.qty}`} t={orderLineText(l)} right={<Amt>{formatInr(l.amount)}</Amt>} />)}
      <Row t={<span style={{ fontWeight: 500, color: '#6A6359' }}>Delivery</span>} right={<Amt style={{ color: '#6A6359' }}>{o.deliveryCharge ? formatInr(o.deliveryCharge) : 'Free'}</Amt>} />
      <Row t="Total" right={<Amt>{formatInr(o.total)}</Amt>} />
    </Card>
  );
  const payBlock = o.status === 'waiting' ? <Note icon="clock">Waiting for the kitchen to confirm your order.</Note> : o.status === 'cancelled' ? null : o.payState === 'refunded' ? (
    <Note icon="check">Your payment was refunded.</Note>
  ) : o.payState === 'paid' ? (
    <Card><Row left={<Ic icon="check" tone="g" />} t="Paid" s="Thank you" right={<Amt>{formatInr(o.total)}</Amt>} /></Card>
  ) : o.payState === 'says_paid' ? (
    <Note icon="check">You said you’ve paid. {o.kitchenName} will tick it once it shows in their bank.</Note>
  ) : (
    <>
      {o.payLink && <Btn label={`Pay ${formatInr(o.total)} by UPI`} go="rupee" href={o.payLink} />}
      <div className="p-links"><button type="button" onClick={() => paid.mutate()} disabled={paid.isPending}>I’ve paid</button></div>
    </>
  );

  if (state.booked && o.status !== 'cancelled') {
    return (
      <Screen gap={20}>
        <div className="p-stack" style={{ alignItems: 'center', textAlign: 'center', gap: 10, paddingTop: 28 }}>
          <span aria-hidden="true" style={{ width: 64, height: 64, borderRadius: '50%', background: '#2F6B3B', boxShadow: '0 0 0 10px #EAF2EB', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>
            <Icon name="check" size={28} sw={2.6} />
          </span>
          <h1 className="p-h1" style={{ marginTop: 10 }}>{o.status === 'waiting' ? 'Request sent' : 'Booked'}, {o.name}</h1>
          <Kick>Order #{o.number} · {formatDay(o.day)}{isMeal(o.menuKind) ? `, ${mealName(o.meal).toLowerCase()}` : ''}</Kick>
        </div>
        {lines}
        {payBlock}
        {state.tell && <Btn2 label="Tell the kitchen on WhatsApp" icon="send" href={state.tell} />}
        <div className="p-links"><Link to={`/o/${token}`} replace state={{}}>Your order: see it, or cancel it</Link></div>
        {err && <span className="p-error" role="alert">{errorMessage(err)}</span>}
        <Foot />
      </Screen>
    );
  }

  const step = o.status === 'delivered' ? 3 : o.status === 'cancelled' ? -1 : o.status === 'waiting' ? 0 : 1;
  return (
    <Screen gap={16}>
      <Top><KitchenChip name={o.kitchenName} /></Top>
      <Title h={`Order #${o.number}`} badge={<Badge tone={o.status === 'cancelled' ? 'r' : o.status === 'delivered' ? '' : 'g'}>{o.status === 'cancelled' ? 'Cancelled' : o.status === 'delivered' ? 'Delivered' : o.status === 'waiting' ? 'Waiting for confirmation' : 'Confirmed'}</Badge>}
        sub={[formatDay(o.day), isMeal(o.menuKind) ? mealName(o.meal).toLowerCase() : null, o.menuName].filter(Boolean).join(' · ')} />
      {step >= 0 && (
        <Card pad>
          <div className="p-trk">
            <span className="on">Booked</span>
            <span className={step >= 1 ? 'on' : ''}>Confirmed</span>
            <span className={step >= 3 ? 'on' : ''}>On the way</span>
            <span className={step >= 3 ? 'on' : ''}>Delivered</span>
          </div>
        </Card>
      )}
      {lines}
      {payBlock}
      {o.canCancel && (sure ? (
        <G2>
          <Btn2 label="Keep it" onClick={() => setSure(false)} />
          <Btn2 label={cancel.isPending ? 'Cancelling…' : 'Yes, cancel'} red onClick={() => cancel.mutate()} />
        </G2>
      ) : <Btn2 label="Cancel this order" icon="close" red onClick={() => setSure(true)} />)}
      {o.canCancel && o.closesAt && <span className="p-sub" style={{ alignSelf: 'center', fontSize: 13 }}>You can cancel until booking closes: {closesText(o.closesAt).replace(/^Closes /, '')}</span>}
      {o.kitchenWhatsApp && <Btn2 label="Message the kitchen" icon="chat" href={waLink(o.kitchenWhatsApp, `Hi ${o.kitchenName}, about my order #${o.number}`)} />}
      {err && <span className="p-error" role="alert">{errorMessage(err)}</span>}
      <Foot />
    </Screen>
  );
}
